import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { CutRollsPayload } from "../models/Rollshop";
import {
	cutRolls,
	getActiveOperators,
	getPassStatus,
	getRollsProduction,
	getRollsetStatusValidChanges,
	updateRollsProduction,
} from "../repositories/RollshopRepository";
import { getRollsetsList } from "../repositories/RollshopOperationsRepository";

// The legacy screen refreshes every 10 seconds: the line and the sync change these rows too.
// React Query keeps the previous objects when nothing changed, so a quiet refresh redraws nothing.
export const ROLLSHOP_REFETCH_MS = 10_000;

const ROLLSETS_KEY = ["rollshopOperations", "rollsets"];
const PRODUCTION_KEY = ["rollshopOperations", "rollsProduction"];

// Same cache as the Rollsets page; this observer also polls.
export const useRollshopRollsets = (isRefreshHeld = false) =>
	useQuery({
		queryKey: ROLLSETS_KEY,
		queryFn: ({ signal }) => getRollsetsList({ signal }),
		refetchInterval: isRefreshHeld ? false : ROLLSHOP_REFETCH_MS,
	});

export const useRollsProduction = (
	rollsetId: number | null,
	isRefreshHeld = false,
) =>
	useQuery({
		queryKey: [...PRODUCTION_KEY, rollsetId],
		queryFn: ({ signal }) =>
			getRollsProduction({ rollsetId: rollsetId ?? 0, signal }),
		enabled: rollsetId != null,
		refetchInterval: isRefreshHeld ? false : ROLLSHOP_REFETCH_MS,
	});

export const useRollsetStatusValidChanges = () =>
	useQuery({
		queryKey: ["rollshopOperations", "rollsetStatusValidChanges"],
		queryFn: ({ signal }) => getRollsetStatusValidChanges({ signal }),
		staleTime: 5 * 60 * 1000,
	});

export const useActiveOperators = () =>
	useQuery({
		queryKey: ["rollshopOperations", "activeOperators"],
		queryFn: ({ signal }) => getActiveOperators({ signal }),
		staleTime: 5 * 60 * 1000,
	});

export const usePassStatus = () =>
	useQuery({
		queryKey: ["rollshopOperations", "passStatus"],
		queryFn: ({ signal }) => getPassStatus({ signal }),
		staleTime: 5 * 60 * 1000,
	});

interface MutationOptions {
	onSuccess?: () => void;
	onError?: (error: Error) => void;
}

export const useCutRolls = ({ onSuccess, onError }: MutationOptions = {}) => {
	const queryClient = useQueryClient();
	const mutation = useMutation({
		mutationFn: (payload: CutRollsPayload) => cutRolls(payload),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ROLLSETS_KEY });
			queryClient.invalidateQueries({ queryKey: PRODUCTION_KEY });
			queryClient.invalidateQueries({
				queryKey: ["rollshopOperations", "rolls"],
			});
			onSuccess?.();
		},
		onError: (error: Error) => onError?.(error),
	});
	return { cutRolls: mutation.mutate, isCutting: mutation.isLoading };
};

export const useUpdatePassStatus = ({
	onSuccess,
	onError,
}: MutationOptions = {}) => {
	const queryClient = useQueryClient();
	const mutation = useMutation({
		mutationFn: updateRollsProduction,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: PRODUCTION_KEY });
			onSuccess?.();
		},
		onError: (error: Error) => onError?.(error),
	});
	return {
		updatePassStatus: mutation.mutate,
		isUpdatingPassStatus: mutation.isLoading,
	};
};
